import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Modal } from '../layout';
import RicambiSelector from '../selectors/Ricambi/RicambiSelector';
import { useOrder } from '../context/OrderContext';
import Swal from 'sweetalert2';

const RicambiPage = () => {
  const navigate = useNavigate();
  const { dispatch } = useOrder();
  const [selectedRicambio, setSelectedRicambio] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  const handleRicambioSelect = (ricambio) => {
    setSelectedRicambio(ricambio);
    setQuantity(1); // Reset quantity when selecting new ricambio
  };

  const handleCloseModal = () => {
    setSelectedRicambio(null);
    setQuantity(1);
  };

  const handleQuantityChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setQuantity('');
      return;
    }
    const value = parseInt(raw);
    if (!isNaN(value) && value > 0) {
      setQuantity(Math.min(9999, value));
    }
  };

  const handleAddToCart = async () => {
    if (!selectedRicambio) {
      Swal.fire({
        icon: 'warning',
        title: 'Attenzione',
        text: 'Seleziona un ricambio prima di procedere'
      });
      return;
    }

    if (quantity < 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Attenzione',
        text: 'Inserisci una quantità valida'
      });
      return;
    }

    try {
      setIsAdding(true);

      // Create a ricambio cart item in the same format as other cart items
      const ricambioCartItem = {
        // Basic required fields
        productCode: selectedRicambio.ricambioCode,
        description: selectedRicambio.description,
        quantity: quantity,

        // Ricambio specific fields
        isRicambioItem: true,
        category: 'ricambi',
        type: 'ricambio',

        // Additional metadata
        ricambioCode: selectedRicambio.ricambioCode,
        addedToCart: true,

        // Processing info (minimal for ricambi)
        processing: {
          supplyType: 'ricambi',
          isComplete: true
        },

        // Wizard steps for ricambi
        wizardSteps: {
          ricambio: {
            code: selectedRicambio.ricambioCode,
            description: selectedRicambio.description,
            quantity: quantity,
            isComplete: true
          }
        }
      };

      // Add directly to cart items array (similar to ADD_ACCESSORY_TO_CART pattern)
      dispatch({
        type: 'ADD_RICAMBIO_TO_CART',
        payload: ricambioCartItem
      });

      // Show success message and ask what to do next
      const result = await Swal.fire({
        icon: 'success',
        title: 'Ricambio aggiunto al carrello!',
        html: `
          <div class="text-center">
            <p class="mb-4"><strong>${selectedRicambio.ricambioCode}</strong></p>
            <p class="mb-4">Quantità: <strong>${quantity}</strong></p>
            <p class="text-gray-600">Cosa vuoi fare ora?</p>
          </div>
        `,
        showCancelButton: true,
        showConfirmButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#10b981',
        confirmButtonText: 'Vai al Carrello',
        cancelButtonText: 'Aggiungi Altri Ricambi',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      if (result.isConfirmed) {
        // User wants to go to cart
        navigate('/order-summary');
      } else {
        // User wants to add more ricambi
        handleCloseModal();
        // Stay on the page to select more items
      }
    } catch (error) {
      console.error('Error adding ricambio to cart:', error);
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: 'Impossibile aggiungere il ricambio al carrello'
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header
        title="Ricambi"
        backText="Categorie"
        backTo="/product-categories"
      />
      <div className="container mx-auto px-4 py-6">
        <RicambiSelector onSelect={handleRicambioSelect} />
      </div>

      {/* Configuration Modal */}
      <Modal
        isOpen={!!selectedRicambio}
        onClose={handleCloseModal}
        title="Configura Ricambio"
        size="md"
      >
        {selectedRicambio && (
          <div>
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 mb-6 border border-orange-200">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="font-bold text-xl text-gray-800 mb-2">{selectedRicambio.ricambioCode}</p>
                <p className="text-gray-700 font-medium">{selectedRicambio.description}</p>
              </div>
            </div>

            {/* Quantity Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3 text-center">
                Quantità Richiesta
              </label>
              <div className="flex items-center justify-center space-x-4">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, (parseInt(quantity) || 1) - 1))}
                  className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                  disabled={!quantity || quantity <= 1}
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>

                <div className="relative">
                  <input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="1"
                    max="9999"
                    value={quantity ?? ''}
                    onChange={handleQuantityChange}
                    className="w-24 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setQuantity((parseInt(quantity) || 0) + 1)}
                  className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Puoi inserire anche manualmente un valore da 1 a 9999
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                disabled={isAdding}
              >
                Annulla
              </button>
              <button
                onClick={handleAddToCart}
                disabled={isAdding || !quantity || quantity === '' || parseInt(quantity) < 1}
                className="px-8 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isAdding && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>{isAdding ? 'Aggiungendo...' : 'Aggiungi al Carrello'}</span>
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RicambiPage;